import { Controller } from "@hotwired/stimulus"

export default class CursorPaginationController extends Controller {
  static targets = ["loader"]

  connect() {
    this.loading = false
    this.loadedUrls = new Set()

    this.createObserver()
    this.observeLoader()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  loaderTargetConnected() {
    if (!this.observer) {
      this.createObserver()
    }

    this.observeLoader()
  }

  createObserver() {
    this.observer?.disconnect()

    this.observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.loadNext()
        }
      },
      {
        root: this.element,
        rootMargin: this.preloadMargin(),
        threshold: 0
      }
    )
  }

  preloadMargin() {
    return window.innerWidth <= 767
      ? "1200px 0px"
      : "5000px 0px"
  }

  observeLoader() {
    if (!this.observer || !this.hasLoaderTarget || this.loading) return

    this.observer.disconnect()
    this.observer.observe(this.loaderTarget)
  }

  async loadNext() {
    if (this.loading || !this.hasLoaderTarget) return

    const loader = this.loaderTarget
    const url = loader.dataset.url

    if (!url || this.loadedUrls.has(url)) return

    this.loading = true
    this.loadedUrls.add(url)
    this.observer?.unobserve(loader)

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html"
        },
        cache: "no-store",
        credentials: "same-origin"
      })

      if (!response.ok) {
        throw new Error(`Pagination failed with ${response.status}`)
      }

      const html = await response.text()

      if (!html.trim()) {
        throw new Error("Empty pagination response")
      }

      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error("Cursor pagination error:", error)

      this.loadedUrls.delete(url)

      requestAnimationFrame(() => {
        this.observeLoader()
      })
    } finally {
      this.loading = false
    }
  }
}